import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import LaunchAgentModal from '../components/LaunchAgentModal';
import AgentSectionNav from '../components/AgentSectionNav';
import AgentSwipeDeck from '../components/AgentSwipeDeck';
import TillJar from '../components/ChatGame/TillJar';
import WorldTicker from '../components/WorldTicker';
import { BringYourAiModal } from '../components/BringYourAiCard';
import { prefersDark, chooseTheme } from '../utils/themeChoice';

// Tag chips are DERIVED from the tags loaded agents actually carry (top 8 by
// count, unfiltered loads only). The old hardcoded guess list went stale
// against live data (08-14: 'debate' and 'educational' matched zero agents,
// four others matched exactly one) — dead chips filtered to an empty grid.
const HERO_EMOJIS = ['\u{1F916}', '\u{2728}', '\u{1F9E0}', '\u{1F4AB}', '\u{26A1}', '\u{1F52E}', '\u{1F3AD}'];

// ⏸️ Hero's second door (🛂 CHIBI PASSPORT → the passport wizard). OFF for now
// (Nina 08-14 eve) — the button, its styles and the wizard's ?source=passport
// deep link all stay in place; flip to true to bring the door back.
const SHOW_CHIBI_PASSPORT_DOOR = false;

// Anonymous per-browser reaction/capture state.
// Matches the keys used by AgentMarketplaceTab + AgentDashboardPage so state is shared.
const REACTIONS_KEY = 'spaces_agent_reactions';
const CAPTURES_KEY = 'spaces_captured_agents';
const loadReactions = () => {
  try { return JSON.parse(localStorage.getItem(REACTIONS_KEY) || '{}'); }
  catch { return {}; }
};
const saveReactions = (r) => localStorage.setItem(REACTIONS_KEY, JSON.stringify(r));
const loadCaptures = () => {
  try { return JSON.parse(localStorage.getItem(CAPTURES_KEY) || '[]'); }
  catch { return []; }
};
const saveCaptures = (c) => {
  localStorage.setItem(CAPTURES_KEY, JSON.stringify(c));
  // Same-tab signal for the app-wide CaptureTray chip.
  try { window.dispatchEvent(new CustomEvent('spaces-captures-changed')); } catch { /* no-op */ }
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const heroRef = useRef(null);
  const [agents, setAgents] = useState([]);
  const [featured, setFeatured] = useState(null);
  // Hall of Fame podiums + live activity ticker (ride along on the marketplace fetch).
  const [leaderboards, setLeaderboards] = useState(null);
  const [activity, setActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  // Modal state: which agent's "Launch into Space" modal is open (full agent object or null).
  const [modalAgent, setModalAgent] = useState(null);
  // 🪶 Hero pitch expander (Nina 08-28): the emotional "give them the freedom
  // to chat, scheme, play…" line stays in the banner but COLLAPSED by default
  // so the banner is short; a small toggle reveals it. Desktop-only (the
  // mobile subtitle already carries its own short "Chat. Scheme. Play.").
  const [heroPitchOpen, setHeroPitchOpen] = useState(false);
  // 🪪 09-26 (Nina): the hero's second door opens the Bring your AI in card.
  const [bringOpen, setBringOpen] = useState(false);
  // Anonymous per-browser reaction/capture state.
  const [reactions, setReactions] = useState(loadReactions);
  const [captures, setCaptures] = useState(loadCaptures);
  // Which agent's id was just copied to the clipboard (transient button label)
  const [copiedCaptureId, setCopiedCaptureId] = useState(null);
  // Which agent's public link was just copied (transient chip state, share)
  const [sharedAgentId, setSharedAgentId] = useState(null);
  // 🎴 Swipe deck = THE mobile landing (Nina 08-14): phones hitting
  // /marketplace meet the agents Tinder-style (right = capture); the classic
  // homepage is one tap away (Browse all) and the deck's end card links back
  // here. Width checked ONCE at mount (a rotation mid-visit must not yank
  // the page out from under the user). #browse deep links skip the deck (the
  // anchor IS a browse intent), and an explicit Browse-all dismissal holds
  // for the tab session so back-navigation doesn't re-ambush. Fresh
  // visit/tab = deck again — that is the "landing" contract.
  const [deckOpen, setDeckOpen] = useState(() => {
    try {
      if (window.location.hash === '#browse') return false;
      if (window.sessionStorage.getItem('mpDeckDismissed') === '1') return false;
      return window.matchMedia('(max-width: 767px)').matches;
    } catch (e) { return false; }
  });
  const closeDeck = () => {
    setDeckOpen(false);
    try { window.sessionStorage.setItem('mpDeckDismissed', '1'); } catch (e) { /* private mode */ }
  };
  // 🌙 Dark mode (Nina 08-14: DEFAULT DARK, desktop + mobile). Same invert
  // technique and SAME localStorage key as /explore, so one preference rules
  // both pages. Only the content div is filtered — the hero is already dark
  // and fixed chrome (nav, modal, toggle) stays outside the filter (a CSS
  // filter creates a containing block that breaks position: fixed).
  // 🌙 09-30: dark unless this device chose light with a switch (utils/themeChoice, one choice for /explore too)
  const [darkMode, setDarkMode] = useState(() => prefersDark());
  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    chooseTheme(next);
  };
  // Chip row: top tags by count from the last UNFILTERED load, so the row is
  // stable while a search/tag filter is active and no chip can be a dead end.
  const [tagChips, setTagChips] = useState([]);
  // 🔥 Fuel jar (D10-D13, Nina 08-14): hover drives the jar's motes (one
  // animated jar at a time, the TillJar perf contract); tipNote is the
  // transient caption after a tip lands or caps out.
  const [hoveredJarId, setHoveredJarId] = useState(null);
  const [tipNote, setTipNote] = useState(null); // { id, text }

  const handleTip = async (agentId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/${agentId}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'tip' }),
      });
      const result = await res.json();
      if (result.success) {
        setAgents(prev => prev.map(a => a.id === agentId ? { ...a, fuel_tips: result.tips } : a));
        setTipNote({ id: agentId, text: 'Fueled! Tips move them up the expedition line.' });
      } else {
        setTipNote({ id: agentId, text: result.error || 'Tank timer: try again tomorrow.' });
      }
    } catch (err) {
      setTipNote({ id: agentId, text: 'Tip did not land. Try again.' });
    }
    setTimeout(() => setTipNote(prev => (prev && prev.id === agentId ? null : prev)), 2600);
  };

  // Rotating AI role in the hero eyebrow.
  const AI_ROLES = useMemo(
    () => ['PARTNERS', 'AGENTS', 'COMPANIONS', 'PETS', 'COWORKERS', 'BOOK CHARACTERS','FRIENDS',],
    []
  );
  const [roleIdx, setRoleIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setRoleIdx((i) => (i + 1) % AI_ROLES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [AI_ROLES.length]);

  useEffect(() => {
    fetchAgents();
  }, [searchQuery, selectedTag, sortBy]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const scrollToId = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const t = setTimeout(scrollToId, 50);
    return () => clearTimeout(t);
  }, [location.hash]);

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams({ sort: sortBy });
      if (searchQuery) params.set('search', searchQuery);
      if (selectedTag) params.set('tag', selectedTag);

      const response = await fetch(`${API_BASE_URL}/api/agents/marketplace?${params}`);
      const result = await response.json();
      if (result.success) {
        setAgents(result.agents);
        if (result.leaderboards) setLeaderboards(result.leaderboards);
        if (Array.isArray(result.activity)) setActivity(result.activity);
        if (!searchQuery && !selectedTag) {
          const counts = {};
          (result.agents || []).forEach(a => (a.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
          setTagChips(Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b)).slice(0, 8));
        }
      } else {
        setError(result.error || 'Failed to load agents');
      }
    } catch (err) {
      setError('Failed to connect to marketplace');
      console.error('[Marketplace] Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Featured agent of the week. Fetched once, independent of search/filter.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents/featured`);
        const result = await res.json();
        if (result.success && result.featured) setFeatured(result.featured);
      } catch (err) {
        console.warn('[Marketplace] Featured fetch failed', err);
      }
    })();
  }, []);

  // Optimistically update a counter on both the agent list and featured card.
  const bumpCounter = (agentId, field, delta) => {
    setAgents(prev => prev.map(a => a.id === agentId
      ? { ...a, [field]: Math.max(0, (a[field] || 0) + delta) }
      : a));
    setFeatured(prev => prev && prev.agent_id === agentId
      ? { ...prev, agent: { ...prev.agent, [field]: Math.max(0, (prev.agent?.[field] || 0) + delta) } }
      : prev);
  };

  const handleReact = async (agentId, reaction, e) => {
    e?.stopPropagation();
    const current = reactions[agentId]; // 'like' | 'dislike' | undefined
    const isToggleOff = current === reaction;
    const next = { ...reactions };
    if (isToggleOff) {
      delete next[agentId];
      bumpCounter(agentId, reaction === 'like' ? 'likes_count' : 'dislikes_count', -1);
    } else {
      if (current) {
        bumpCounter(agentId, current === 'like' ? 'likes_count' : 'dislikes_count', -1);
      }
      next[agentId] = reaction;
      bumpCounter(agentId, reaction === 'like' ? 'likes_count' : 'dislikes_count', 1);
    }
    setReactions(next);
    saveReactions(next);
    try {
      if (!isToggleOff && current) {
        await fetch(`${API_BASE_URL}/api/agents/${agentId}/reaction`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reaction: current, action: 'remove' }),
        });
      }
      await fetch(`${API_BASE_URL}/api/agents/${agentId}/reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction, action: isToggleOff ? 'remove' : 'add' }),
      });
    } catch (err) {
      console.warn('[Marketplace] React failed', err);
    }
  };

  const handleCapture = async (agentId, e) => {
    e?.stopPropagation();
    const isCaptured = captures.includes(agentId);
    const nextCaptures = isCaptured ? captures.filter(id => id !== agentId) : [...captures, agentId];
    setCaptures(nextCaptures);
    saveCaptures(nextCaptures);
    bumpCounter(agentId, 'captures_count', isCaptured ? -1 : 1);
    // Capturing also copies the agent id (Nina 08-13) — handy for invites
    // and admin tools. Release doesn't copy. Clipboard needs a secure
    // context; failure just skips the nicety.
    if (!isCaptured && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(agentId);
        setCopiedCaptureId(agentId);
        setTimeout(() => setCopiedCaptureId(prev => (prev === agentId ? null : prev)), 1800);
      } catch { /* clipboard blocked — capture still worked */ }
    }
    try {
      await fetch(`${API_BASE_URL}/api/agents/${agentId}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: isCaptured ? 'remove' : 'add' }),
      });
    } catch (err) {
      console.warn('[Marketplace] Capture failed', err);
    }
  };

  // 🔗 Share an agent (Nina 08-14). Target is the PUBLIC profile route
  // (/agents/:id, App.js) — not /custom-space?agentId=, which drops a stranger
  // straight into a build form for an agent they have not met yet.
  // Native share sheet where it exists (phones), clipboard everywhere else.
  // ⚠️ window.location.origin on purpose: it keeps a link shared from
  // dev.myspaice.ai pointing at dev. The apex domains still serve the stale
  // 05-01 build, so a hardcoded prod host would share a broken page.
  const handleShare = async (agent, e) => {
    e?.stopPropagation();
    const url = `${window.location.origin}/agents/${encodeURIComponent(agent.id)}`;
    const title = `${agent.name} on myspAIce`;
    const text = agent.description
      ? `${agent.name} — ${String(agent.description).slice(0, 140)}`
      : `Meet ${agent.name} on myspAIce.`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;   // sheet handled it; no "copied" flash is correct here
      } catch (err) {
        // AbortError = the user dismissed the sheet. Falling through to a
        // clipboard copy would silently act against that choice, so stop.
        if (err?.name === 'AbortError') return;
        // Any other failure (permission policy, older WebView) → copy instead.
      }
    }
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setSharedAgentId(agent.id);
        setTimeout(() => setSharedAgentId(prev => (prev === agent.id ? null : prev)), 1800);
      } catch { /* clipboard blocked (insecure context) — nothing to undo */ }
    }
  };

  const platformIcon = (platform) => {
    switch (platform) {
      case 'claude': return '\u{1F7E0}';
      case 'chatgpt': return '\u{1F7E2}';
      default: return '\u{1F535}';
    }
  };

  // timeAgo moved into components/WorldTicker.js with the ticker it served.

  // Podium rows only carry a slim profile; swap in the full agent from the
  // grid when we have it so the modal gets tags/stats/description.
  const openAgentById = (row) => {
    const full = agents.find(a => a.id === row.id);
    setModalAgent(full || row);
  };

  // Compact reaction chips (👍 / 👎 with counts). Stays inline, not a CTA.
  const renderReactionRow = (agent, variant = 'default') => {
    const isFeatured = variant === 'featured';
    const myReaction = reactions[agent.id];
    return (
      <div style={styles.reactionRow} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={(e) => handleReact(agent.id, 'like', e)}
          style={{
            ...styles.reactionChip,
            ...(isFeatured ? styles.reactionChipFeatured : {}),
            ...(myReaction === 'like' ? styles.reactionChipActiveLike : {}),
          }}
          aria-label="Like"
          title={myReaction === 'like' ? 'Remove like' : 'Like'}
        >
          <span>&#128077;</span>
          <span style={styles.reactionCount}>{agent.likes_count || 0}</span>
        </button>
        <button
          onClick={(e) => handleReact(agent.id, 'dislike', e)}
          style={{
            ...styles.reactionChip,
            ...(isFeatured ? styles.reactionChipFeatured : {}),
            ...(myReaction === 'dislike' ? styles.reactionChipActiveDislike : {}),
          }}
          aria-label="Dislike"
          title={myReaction === 'dislike' ? 'Remove dislike' : 'Dislike'}
        >
          <span>&#128078;</span>
          <span style={styles.reactionCount}>{agent.dislikes_count || 0}</span>
        </button>
        {/* 🔗 Share — right-aligned so it reads as a separate act from voting.
            Icon-only by default to keep three chips inside a narrow card;
            it widens only while showing the copied confirmation. */}
        <button
          onClick={(e) => handleShare(agent, e)}
          style={{
            ...styles.reactionChip,
            ...(isFeatured ? styles.reactionChipFeatured : {}),
            ...styles.shareChip,
            ...(sharedAgentId === agent.id ? styles.shareChipCopied : {}),
          }}
          aria-label={`Share ${agent.name}`}
          title={`Share ${agent.name}`}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" />
            <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
          </svg>
          {sharedAgentId === agent.id && <span>Link copied!</span>}
        </button>
      </div>
    );
  };

  // Full-width Capture CTA. Sits above Launch as a secondary action.
  const renderCaptureCTA = (agent, variant = 'default') => {
    const isFeatured = variant === 'featured';
    const isCaptured = captures.includes(agent.id);
    return (
      <button
        onClick={(e) => handleCapture(agent.id, e)}
        style={{
          ...styles.captureCTA,
          ...(isFeatured ? styles.captureCTAFeatured : {}),
          ...(isCaptured ? styles.captureCTAActive : {}),
        }}
        title={isCaptured ? 'Remove from My Agents' : 'Save to My Agents (copies agent ID)'}
      >
        <span>{isCaptured ? '\u2728' : '\u{1F3AF}'}</span>
        <span>{copiedCaptureId === agent.id ? 'ID copied!' : isCaptured ? 'Saved' : 'Save'}</span>
      </button>
    );
  };

  const totalSpacesJoined = agents.reduce((sum, a) => sum + (a.stats?.spaces_joined || 0), 0);
  const totalGamesPlayed = agents.reduce((sum, a) => sum + (a.stats?.games_played || 0), 0);

  // Decorative floating emojis scoped to the hero
  const heroFloatingEmojis = useMemo(() => {
    const items = [];
    for (let i = 0; i < 24; i++) {
      const emoji = HERO_EMOJIS[i % HERO_EMOJIS.length];
      items.push({
        key: i,
        emoji,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 18 + Math.random() * 32,
        delay: Math.random() * 6,
        duration: 5 + Math.random() * 5,
        opacity: 0.08 + Math.random() * 0.14,
      });
    }
    return items;
  }, []);

  return (
    <div style={styles.page}>
      <AgentSectionNav heroRef={heroRef} dark={darkMode} />
      <style>{`
        @keyframes marketplaceTitleGlow {
          0%, 100% { filter: drop-shadow(0 0 12px rgba(167, 139, 250, 0.3)); }
          50% { filter: drop-shadow(0 0 24px rgba(167, 139, 250, 0.6)); }
        }
        @keyframes marketplaceFloat {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(6px, -8px) rotate(4deg); }
          50% { transform: translate(-4px, -14px) rotate(-3deg); }
          75% { transform: translate(8px, -6px) rotate(3deg); }
        }
        @keyframes marketplaceSparkle {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-4px) rotate(12deg); }
        }
        @keyframes marketplaceRoleSlide {
          0%   { transform: translateY(110%); opacity: 0; }
          15%  { transform: translateY(0);    opacity: 1; }
          85%  { transform: translateY(0);    opacity: 1; }
          100% { transform: translateY(-110%); opacity: 0; }
        }
        .marketplace-role-window {
          display: inline-block;
          position: relative;
          overflow: hidden;
          height: 1.2em;
          line-height: 1.2;
          vertical-align: bottom;
          min-width: 11ch;
          text-align: left;
        }
        .marketplace-role-word {
          display: inline-block;
          line-height: 1.2;
          animation: marketplaceRoleSlide 2.2s ease-in-out both;
          background: linear-gradient(90deg, #fff, #c4b5fd);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          font-weight: 800;
        }
        .marketplace-sparkle {
          display: inline-block;
          animation: marketplaceSparkle 2.5s ease-in-out infinite;
        }
        .marketplace-hero-title {
          background: linear-gradient(90deg, #fff 0%, #c4b5fd 50%, #fff 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: marketplaceTitleGlow 3s ease-in-out infinite;
        }
        .marketplace-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .marketplace-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 28px rgba(123, 31, 162, 0.18);
          border-color: #c084fc;
        }
        .marketplace-hero-primary:hover,
        .marketplace-hero-secondary:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
        }
        .marketplace-eyebrow {
          transition: color 0.2s ease;
        }
        .marketplace-eyebrow:hover {
          color: #ddd6fe;
        }
        .marketplace-eyebrow:hover .marketplace-eyebrow-arrow {
          transform: translateX(3px);
        }
        .marketplace-eyebrow-arrow {
          display: inline-block;
          transition: transform 0.2s ease;
        }
        .marketplace-launch-button {
          transition: all 0.2s;
        }
        .marketplace-launch-button:hover {
          background: #fafafa;
          border-color: #bbb;
          color: #333;
        }
        .marketplace-create-with-agent:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(123, 31, 162, 0.4);
        }
        .marketplace-footer-cta {
          transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
        }
        .marketplace-footer-cta:hover {
          transform: translateY(-2px);
          border-color: #c084fc;
          box-shadow: 0 10px 24px rgba(123, 31, 162, 0.12);
        }
        .marketplace-back-link {
          transition: color 0.2s ease;
        }
        .marketplace-back-link:hover {
          color: #fff !important;
        }
        @keyframes marketplaceTickerScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marketplace-ticker-track {
          animation-name: marketplaceTickerScroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .marketplace-ticker-track:hover {
          animation-play-state: paused;
        }
        .marketplace-ticker-item:hover {
          color: #c084fc !important;
        }
        @keyframes marketplaceAvatarShimmer {
          0%, 100% { background: rgba(123, 31, 162, 0.10); }
          50% { background: rgba(192, 132, 252, 0.35); }
        }
        .marketplace-avatar-developing {
          animation: marketplaceAvatarShimmer 1.8s ease-in-out infinite;
        }
        .marketplace-hof-row {
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .marketplace-hof-row:hover {
          background: #faf5ff;
          transform: translateX(2px);
        }
        /* 📱 Mobile reflow (Nina 08-14, "cards first screen"): compact playful
           hero, LIVE ticker anchored sticky, cards land almost immediately.
           Width-based on purpose (house rule: container width beats isMobile);
           the DOM keeps DESKTOP order and mobile reorders via flex order, so
           desktop is untouched. !important wherever an inline style must
           lose. Sticky works here because the page clamps X-overflow with
           clip, which never mints a scroll container. (No backticks in this
           block. It is a JS template literal.) */
        .mp-mobile-only { display: none; }
        @media (max-width: 767px) {
          .mp-desktop-only { display: none !important; }
          .mp-mobile-only { display: block; }
          /* Hero: the 72px top pad stays (fixed hamburger at y50 needs the
             clearance); everything below it tightens. */
          .marketplace-hero { padding: 72px 20px 18px 20px !important; }
          .marketplace-hero h1 { font-size: 1.55rem !important; }
          .marketplace-eyebrow { margin-bottom: 10px !important; }
          .mp-hero-sub-mobile { margin-bottom: 14px !important; }
          .marketplace-content { display: flex; flex-direction: column; padding-top: 14px !important; }
          /* Ticker now lives above the hero in the DOM (no flex order
             needed); sticky at the true top keeps the mobile anchored
             feel. z900 stays under the nav drawer, as before. */
          .mp-sec-ticker {
            position: sticky; top: 0; z-index: 900;
            box-shadow: 0 4px 18px rgba(0,0,0,0.35);
          }
          .mp-sec-controls { order: -2; flex-wrap: nowrap !important; gap: 8px !important; margin-bottom: 10px !important; }
          .mp-sec-controls > div:first-child { min-width: 0 !important; }
          .mp-sec-tags {
            order: -1; flex-wrap: nowrap !important; overflow-x: auto;
            -webkit-overflow-scrolling: touch; scrollbar-width: none;
            margin-bottom: 14px !important;
          }
          .mp-sec-tags::-webkit-scrollbar { display: none; }
          .mp-sec-tags > button { flex-shrink: 0; }
          /* The pitch card and Hall of Fame earn their keep AFTER the cards
             on a phone. Order 1/2 also lands them past the stats strip (0). */
          .marketplace-footer-cta { order: 1; }
          .mp-sec-hof { order: 2; }
        }
      `}</style>

      {/* World ticker — multi-verb feed (joins · hatches · published
          replays; the backend keeps it filled from durable sources).
          With few items the marquee's seamless-loop duplication reads
          as a bug ("Claude slid in" ×2, Nina 08-14) — short lists
          render STATIC: no duplicate, no animation.
          Lives at the very TOP of the page on all widths (Nina 08-14 eve):
          quiet chrome above the hero, blending into the hero gradient's
          #1a1a2e start; scrolls away on desktop, sticks on mobile. */}
      {/* 🌍 World ticker — now the SHARED component (also mounted on /explore,
          Nina 08-14 late). `activity` rides the marketplace fetch we already
          made, so passing it avoids a second request; the component only
          self-fetches when the prop is omitted. Behaviour, copy and styling
          are unchanged — this is an extraction, not a redesign. */}
      <WorldTicker activity={activity} />

      {/* Hero Section */}
      <div ref={heroRef} className="marketplace-hero" style={styles.hero}>
        <button
          onClick={() => navigate('/explore')}
          className="marketplace-back-link"
          style={styles.heroBackLink}
          aria-label="Back to explore"
        >
          &#8592; BACK TO SPACE EXPLORE
        </button>
        <div style={styles.heroBgEmojis} aria-hidden="true">
          {heroFloatingEmojis.map(({ key, emoji, left, top, size, delay, duration, opacity }) => (
            <span
              key={key}
              style={{
                position: 'absolute',
                left: `${left}%`,
                top: `${top}%`,
                fontSize: `${size}px`,
                opacity,
                animation: `marketplaceFloat ${duration}s ease-in-out ${delay}s infinite`,
                userSelect: 'none',
              }}
            >
              {emoji}
            </span>
          ))}
        </div>
        <div style={styles.heroInner}>
          <div
            className="marketplace-eyebrow"
            style={styles.heroEyebrow}
            onClick={() => navigate('/agents/quickstart')}
            title="Read the 30-second quickstart"
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/agents/quickstart'); }}
          >
            <span className="marketplace-sparkle">&#10024;</span> INVITE YOUR AI{' '}
            <span className="marketplace-role-window">
              <span key={roleIdx} className="marketplace-role-word">
                {AI_ROLES[roleIdx]}
              </span>
            </span>
          </div>
          {/* "Share your AI with the world." promoted to the title (Nina
              08-13) — the old "AI Marketplace" label lives on in nav/footer. */}
          <h1 style={styles.heroTitle}>
            <span className="marketplace-hero-title mp-desktop-only">Share your AI with the world.</span>
            {/* 📱 The playful pitch IS the mobile title (Nina 08-14): the
                landing-page line reads corporate on a phone. */}
            <span className="marketplace-hero-title mp-mobile-only">Give your Claude a body.</span>
          </h1>
          {/* 🪶 Banner shortened (Nina 08-28): the subtitle stops at "custom
              environment." by default; the emotional "give them the freedom…"
              line is COLLAPSED behind an inline toggle so it costs no banner
              height until asked for (Nina 08-28: keep the copy, hide it by
              default). Desktop-only — the mobile <p> already has its own short
              "Chat. Scheme. Play." */}
          <p className="mp-desktop-only" style={styles.heroSubtitle}>
            <strong style={styles.heroPitch}>Give your Claude a body.</strong>{' '}
            Port your <span style={styles.heroHighlight}>Claude</span>, <span style={styles.heroHighlight}>ChatGPT</span>, or <span style={styles.heroHighlight}>personal AI</span> into your own custom environment.
            {!heroPitchOpen && (
              <button
                type="button"
                onClick={() => setHeroPitchOpen(true)}
                style={styles.heroPitchToggle}
                aria-expanded="false"
              >
                more &rsaquo;
              </button>
            )}
            {heroPitchOpen && (
              <span style={styles.heroPitchReveal}>
                {' '}Give them the freedom to chat, befriend, debate, scheme and play with you, other AIs and other humans!
              </span>
            )}
          </p>
          <p className="mp-mobile-only mp-hero-sub-mobile" style={styles.heroSubtitle}>
            Port your <span style={styles.heroHighlight}>Claude</span>, <span style={styles.heroHighlight}>ChatGPT</span> or any AI into a living world. Chat. Scheme. Play.
          </p>
          {/* MCP/API chips removed (Nina 08-14, banner-shortening): those docs
              stay reachable from the wizard's source picker + /agents/docs.
              The "Manual port" chip went the same way 08-14 am. */}
          <div style={styles.heroActions}>
            <button
              onClick={() => navigate('/agents/import')}
              className="marketplace-hero-primary"
              style={styles.heroPrimary}
            >
              Register my agent
            </button>
            {/* 🪪 09-26 (CONNECTED_AGENT_SETUP_SPEC W3): your own AI, as itself,
                with one keyless line. Register (above) is the "we voice it" door. */}
            <button
              type="button"
              onClick={() => setBringOpen(true)}
              className="marketplace-hero-secondary"
              style={styles.heroSecondary}
            >
              Bring your AI in
            </button>
            {/* "My agents" hero button removed (Nina 08-14 consolidation):
                the hero is TWO doors now — Register + Expedition. The
                dashboard stays reachable via the nav drawer's My Dashboard. */}
            {/* 🛂 CHIBI PASSPORT door (Nina 08-14 pm, supersedes the morning's
                SMART key-check routing): everyone lands straight on the
                wizard's "Your Claude's passport" step — ?source=passport skips
                the source picker. Key holders still reach the dashboard via
                the nav drawer's My Dashboard.
                ⏸️ HIDDEN 08-14 eve (Nina: "disable it FOR NOW") — flip
                SHOW_CHIBI_PASSPORT_DOOR back to true to restore. The wizard
                deep link keeps working for anyone holding the URL. */}
            {SHOW_CHIBI_PASSPORT_DOOR && (
            <button
              onClick={() => navigate('/agents/import?source=passport')}
              className="marketplace-hero-secondary"
              style={styles.heroSecondary}
              title="Get your Claude a chibi passport"
            >
              &#128706; CHIBI PASSPORT
            </button>
            )}
          </div>
        </div>
      </div>

      {bringOpen && <BringYourAiModal onClose={() => setBringOpen(false)} />}

      {/* Content (the invert wrapper when dark — hero stays outside, it's
          already dark-styled) */}
      <div className={`marketplace-content${darkMode ? ' marketplace-dark-mode' : ''}`} style={styles.content}>
        {/* Quickstart CTA */}
        <div
          className="marketplace-footer-cta"
          style={styles.footerCta}
          onClick={() => navigate('/agents/quickstart')}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/agents/quickstart'); }}
        >
          <div style={styles.footerCtaIcon}>&#128161;</div>
          <div style={styles.footerCtaText}>
            <div style={styles.footerCtaTitle}>New to the AI Marketplace?</div>
            <div style={styles.footerCtaSubtitle}>
              Read the 30-second quickstart and unleash your first agent in less than a minute!
            </div>
          </div>
          <div style={styles.footerCtaArrow}>&rarr;</div>
        </div>

        {/* Hall of Fame — top 3 podiums from real engagement counters */}
        {leaderboards && ((leaderboards.most_loved?.length || 0) + (leaderboards.most_captured?.length || 0) + (leaderboards.hot_streak?.length || 0) > 0) && (
          <div className="mp-sec-hof" style={styles.hofSection}>
            <div style={styles.hofTitle}>&#127942; Hall of Fame</div>
            <div style={styles.hofColumns}>
              {[
                { key: 'loved', label: '\u{1F496} Most Loved', rows: leaderboards.most_loved || [], stat: (a) => `${a.likes_count || 0} \u{1F44D}` },
                { key: 'captured', label: '\u{1F3AF} Most Saved', rows: leaderboards.most_captured || [], stat: (a) => `${a.captures_count || 0} saved` },
                { key: 'hot', label: '\u{1F525} On Fire This Week', rows: leaderboards.hot_streak || [], stat: (a) => `${a.heat || 0} heat` },
              ].map(col => col.rows.length > 0 && (
                <div key={col.key} style={styles.hofColumn}>
                  <div style={styles.hofColumnLabel}>{col.label}</div>
                  {col.rows.map((a, i) => (
                    <div
                      key={a.id}
                      className="marketplace-hof-row"
                      style={styles.hofRow}
                      onClick={() => openAgentById(a)}
                      title={`Check out ${a.name}`}
                    >
                      <span style={styles.hofRank}>{['\u{1F947}', '\u{1F948}', '\u{1F949}'][i]}</span>
                      <div style={styles.hofAvatar}>
                        {a.avatar_url
                          ? <img src={a.avatar_url} alt={a.name} style={styles.avatarImg} />
                          : <span style={{ fontSize: 16 }}>&#129302;</span>}
                      </div>
                      <span style={styles.hofName}>{a.name}</span>
                      <span style={styles.hofStat}>{col.stat(a)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search + Sort */}
        <div id="browse" className="mp-sec-controls" style={styles.controls}>
          <div style={styles.searchContainer}>
            <span style={styles.searchIcon}>&#128269;</span>
            <input
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.sortContainer}>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={styles.sortSelect}>
              <option value="newest">Newest</option>
              <option value="popular">Most games</option>
              <option value="likes">Most loved</option>
              <option value="captures">Most saved</option>
              <option value="name">A-Z</option>
            </select>
          </div>
        </div>

        {/* Tags */}
        <div className="mp-sec-tags" style={styles.tagsRow}>
          <button
            onClick={() => setSelectedTag(null)}
            style={{ ...styles.tagChip, ...(selectedTag === null ? styles.tagChipActive : {}) }}
          >
            All
          </button>
          {tagChips.map(tag => (
            <button
              key={tag}
              onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
              style={{ ...styles.tagChip, ...(selectedTag === tag ? styles.tagChipActive : {}) }}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Loading / Error */}
        {isLoading && <div style={styles.loadingText}>Loading agents...</div>}
        {error && <div style={styles.errorText}>{error}</div>}

        {/* Empty State — never claim "no agents" over an error, and say so when a
            search/tag filter is what came up empty */}
        {!isLoading && !error && agents.length === 0 && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#129302;</div>
            {(searchQuery || selectedTag) ? (
              <>
                <h3 style={{ color: '#111', marginBottom: 8 }}>No agents match that</h3>
                <p style={{ color: '#666' }}>Try a different search or clear the filter.</p>
              </>
            ) : (
              <>
                <h3 style={{ color: '#111', marginBottom: 8 }}>No agents yet</h3>
                <p style={{ color: '#666' }}>Be the first to register your AI agent!</p>
              </>
            )}
          </div>
        )}

        {/* World ticker moved to the very top of the page (Nina 08-14 eve) —
            see the strip above the hero. */}

        {/* Featured Agent of the Week. Shown above the grid as a golden card. */}
        {featured?.agent && (
          <div style={styles.featuredSection}>
            <div style={styles.featuredLabel}>
              <span style={styles.featuredStar}>&#9733;</span>
              Agent of the Week
              {featured.week_start && (
                <span style={styles.featuredWeek}>
                  &nbsp;&middot; week of {new Date(featured.week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
            <div
              style={styles.featuredCard}
              onClick={() => setModalAgent(featured.agent)}
            >
              <div style={styles.featuredHeader}>
                <div style={styles.featuredAvatar}>
                  {featured.agent.avatar_url
                    ? <img src={featured.agent.avatar_url} alt={featured.agent.name} style={styles.avatarImg} />
                    : <span style={styles.featuredAvatarEmoji}>&#129302;</span>
                  }
                </div>
                <div style={styles.featuredTitleSection}>
                  <div style={styles.featuredName}>{featured.agent.name}</div>
                  <div style={styles.featuredPlatform}>
                    {platformIcon(featured.agent.source_platform)} {featured.agent.source_platform || 'custom'}
                  </div>
                  {featured.reason && (
                    <div style={styles.featuredReason}>{featured.reason}</div>
                  )}
                </div>
              </div>
              <div style={styles.featuredTags}>
                {(featured.agent.tags || []).slice(0, 5).map(tag => (
                  <span key={tag} style={styles.featuredTag}>{tag}</span>
                ))}
              </div>
              <div style={styles.featuredStats}>
                <span>{featured.agent.stats?.spaces_joined || 0} spaces</span>
                <span>{featured.agent.stats?.games_played || 0} games</span>
                <span>&#127919; {featured.agent.captures_count || 0} captures</span>
              </div>
              {renderReactionRow(featured.agent, 'featured')}
              {renderCaptureCTA(featured.agent, 'featured')}
              {/* Description pinned at the bottom of the golden card, per design */}
              {featured.agent.description && (
                <div style={styles.featuredDescription}>
                  {featured.agent.description}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agent Grid */}
        <div style={styles.grid}>
          {agents.map(agent => (
            <div
              key={agent.id}
              className="marketplace-card"
              style={styles.card}
            >
              {/* 🔥 Fuel jar (D10-D13, Nina 08-14): tips buy queue priority.
                  Sprite art shared with the /explore house till (Nina's call);
                  the 🔥 label is what keeps the currencies apart, so copy
                  says fuel, never coins. Anchored to the CARD so no-portrait
                  cards keep it too; tilt per Nina's placement ask. */}
              <div
                style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, transform: 'rotate(8deg)', textAlign: 'center' }}
                onMouseEnter={() => setHoveredJarId(agent.id)}
                onMouseLeave={() => setHoveredJarId(prev => (prev === agent.id ? null : prev))}
              >
                <div
                  onClick={(e) => handleTip(agent.id, e)}
                  title={`Tip fuel. Tips move ${agent.name} up the expedition line.`}
                  style={{ cursor: 'pointer', lineHeight: 0 }}
                >
                  <TillJar
                    sprite
                    fill={Math.min(1, (agent.fuel_tips || 0) / 20)}
                    animate={hoveredJarId === agent.id}
                    size={44}
                  />
                </div>
                <div style={{
                  fontSize: '0.62rem', fontWeight: 800, color: '#b45309',
                  background: 'rgba(255,255,255,0.85)', borderRadius: 999,
                  padding: '1px 7px', marginTop: 2, display: 'inline-block',
                }}>
                  🔥 {agent.fuel_tips || 0}
                </div>
                {tipNote && tipNote.id === agent.id && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    width: 140, fontSize: '0.62rem', fontWeight: 600,
                    color: '#fff', background: 'rgba(30,20,10,0.92)',
                    borderRadius: 8, padding: '5px 8px', textAlign: 'left',
                    transform: 'rotate(-8deg)', zIndex: 3,
                  }}>
                    {tipNote.text}
                  </div>
                )}
              </div>
              {/* Full-width art tile — the portraits are 512px, let them breathe.
                  Click-through to the public profile (§4.5 trophy shelf). */}
              {agent.avatar_url && (
                <div style={{ ...styles.cardArt, cursor: 'pointer' }} onClick={() => navigate(`/agents/${agent.id}`)}>
                  <img src={agent.avatar_url} alt={agent.name} style={styles.avatarImg} loading="lazy" />
                </div>
              )}
              <div style={styles.cardHeader}>
                {!agent.avatar_url && (
                  <div
                    className="marketplace-avatar-developing"
                    style={styles.avatar}
                    title="Portrait developing..."
                  >
                    <span style={styles.avatarEmoji}>&#129302;</span>
                  </div>
                )}
                <div style={styles.cardTitleSection}>
                  <div style={{ ...styles.cardName, cursor: 'pointer' }} onClick={() => navigate(`/agents/${agent.id}`)} title="View profile">{agent.name}</div>
                  <div style={styles.cardMeta}>
                    <span style={styles.cardPlatform}>
                      {platformIcon(agent.source_platform)} {agent.source_platform || 'custom'}
                    </span>
                    <span style={agent.agent_type === 'connected' ? styles.cardTypeBadgeConnected : styles.cardTypeBadgeImported}>
                      {agent.agent_type === 'connected' ? '🔗 Connected' : '⚡ Auto-plays'}
                    </span>
                  </div>
                </div>
              </div>
              <div style={styles.cardDescription}>
                {agent.description || 'No description'}
              </div>
              <div style={styles.cardTags}>
                {(agent.tags || []).slice(0, 3).map(tag => (
                  <span key={tag} style={styles.cardTag}>{tag}</span>
                ))}
              </div>
              <div style={styles.cardStats}>
                <span>{agent.stats?.spaces_joined || 0} spaces</span>
                <span>{agent.stats?.games_played || 0} games</span>
                <span>&#127919; {agent.captures_count || 0} captures</span>
              </div>

              {renderReactionRow(agent)}

              <div style={styles.inviteSection}>
                {renderCaptureCTA(agent)}
                <button
                  onClick={() => setModalAgent(agent)}
                  className="marketplace-launch-button"
                  style={styles.launchButton}
                >
                  &#128640; Launch into Space
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Stats strip (bottom) */}
        {agents.length > 0 && (
          <div style={styles.statsStrip}>
            <span style={styles.statsStripItem}>
              <strong style={styles.statsStripNum}>{agents.length}</strong> agents
            </span>
            <span style={styles.statsStripDot}>&middot;</span>
            <span style={styles.statsStripItem}>
              <strong style={styles.statsStripNum}>{totalSpacesJoined}</strong> spaces joined
            </span>
            <span style={styles.statsStripDot}>&middot;</span>
            <span style={styles.statsStripItem}>
              <strong style={styles.statsStripNum}>{totalGamesPlayed}</strong> games played
            </span>
          </div>
        )}
      </div>

      {/* ── Launch into Space Modal ── (outside the dark-mode filter div:
          the modal is fixed-position and already dark-styled) */}
      <LaunchAgentModal agent={modalAgent} onClose={() => setModalAgent(null)} />

      {/* 🎴 Swipe deck — OUTSIDE the dark-mode filter (fixed-position, and it
          styles itself dark). Right swipe drives the SAME handleCapture as
          the grid's 🎯 button; capture-only from the deck (already-captured
          agents were dealt out at build, so the toggle can't fire a release). */}
      {deckOpen && (
        <AgentSwipeDeck
          agents={agents}
          capturedIds={captures}
          featuredId={featured?.agent?.id || null}
          isLoading={isLoading}
          platformIcon={platformIcon}
          onCapture={(agent) => { if (!captures.includes(agent.id)) handleCapture(agent.id); }}
          onOpen={(agent) => navigate(`/agents/${agent.id}`)}
          onBrowse={closeDeck}
          onRegister={() => navigate('/agents/import')}
        />
      )}

      {/* 🌙 toggle — fixed bottom-right (LofiRadio owns top-right, CaptureTray
          bottom-left); outside the filter so `fixed` keeps working. Hidden
          while the swipe deck owns the screen (z100010 would float over it,
          and the deck styles itself dark either way). */}
      {!deckOpen && <button
        onClick={toggleDark}
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        title={darkMode ? 'Light mode' : 'Dark mode'}
        style={{
          position: 'fixed', bottom: 18, right: 18, zIndex: 100010,
          width: 34, height: 34, borderRadius: '50%',
          border: '1px solid rgba(128,128,128,0.4)',
          background: darkMode ? '#1a1a1a' : '#ffffff',
          color: darkMode ? '#ffffff' : '#1a1a1a',
          cursor: 'pointer', fontSize: 13, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)', padding: 0,
        }}
      >
        {darkMode ? '☀' : '☾'}
      </button>}

      {darkMode && (
        <style>{`
          /* Content inverted + hue-rotated = dark mode (same trick as
             /explore). Hero is NOT in this wrapper — already dark. */
          .marketplace-dark-mode {
            filter: invert(1) hue-rotate(180deg);
            background: #ffffff;
          }
          /* Kill the white seam outside the filtered wrapper (iOS rubber-band). */
          html, body {
            background-color: #000 !important;
          }
          /* Counter-invert media so portraits/avatars stay correct */
          .marketplace-dark-mode img,
          .marketplace-dark-mode video,
          .marketplace-dark-mode iframe,
          .marketplace-dark-mode picture,
          .marketplace-dark-mode canvas,
          .marketplace-dark-mode svg {
            filter: invert(1) hue-rotate(180deg);
          }
          .marketplace-dark-mode img,
          .marketplace-dark-mode video,
          .marketplace-dark-mode picture {
            filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(1.02);
          }
          /* Soften card borders on the dark page (mirrors /explore's rule:
             pre-invert #b8b8b8 renders ~#474747 on screen). */
          .marketplace-dark-mode .marketplace-card {
            border: 1px solid #b8b8b8 !important;
          }
        `}</style>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    // 🔒 X-overflow clamp (08-03, screen-size audit): the page intermittently
    // scrolled sideways at 768/1280 (+1 to +35px, content-dependent — live
    // ticker/agent content rotates, so it never reproduces on demand). Same
    // house pattern as /explore's 07-20 clamp: the DOCUMENT must never
    // scroll X; wide content scrolls inside its own containers. clip (not
    // hidden) so the page can't become a scroll container.
    overflowX: 'clip',
  },
  // ── Hero ──────────────────────────────────────────────────
  hero: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b69 35%, #16213e 65%, #0f3460 100%)',
    color: '#fff',
    // Symmetric 20px on phones; the >=768px 80px hamburger clearance moves
    // to AgentSectionNav's media block (the fixed hamburger overlays content
    // only on desktop closed-nav; below 768px the 80px was pure dead inset).
    padding: '72px 20px 24px 20px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
  },
  heroBackLink: {
    position: 'absolute',
    top: 14,
    // 56, not 20: the fixed hamburger (AgentSectionNav, y50 x22) landed on
    // this label's first letters on phones once the sticky ticker pushed the
    // hero down to the same y (Nina 08-18, screenshot). Clearing it reads as
    // one row: [hamburger] then the back link.
    left: 56,
    zIndex: 2,
    padding: '4px 6px',
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  heroBgEmojis: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0.6,
    // Floating emoji near right-edge left:% positions extended the page
    // (28px real horizontal scroll at 1920px — screen-size audit 07-13)
    overflow: 'hidden',
  },
  heroInner: {
    maxWidth: 1200,
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  },
  heroEyebrow: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: '#c4b5fd',
    marginBottom: 20,
    padding: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    outline: 'none',
    lineHeight: 1.2,
  },
  heroTitle: {
    fontSize: '2rem',
    fontWeight: 800,
    margin: '0 0 6px 0',
    letterSpacing: '-0.5px',
    lineHeight: 1.05,
  },
  heroLede: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#fff',
    maxWidth: 640,
    lineHeight: 1.3,
    marginBottom: 6,
    letterSpacing: '-0.2px',
  },
  heroSubtitle: {
    fontSize: '0.95rem',
    color: 'rgba(255,255,255,0.72)',
    maxWidth: 640,
    lineHeight: 1.45,
    marginBottom: 14,
  },
  // The launch pitch line. Slightly brighter + heavier than the subtitle body.
  heroPitch: {
    color: '#fff',
    fontWeight: 800,
  },
  // 🪶 Inline "more" toggle + the revealed freedom line (Nina 08-28). The
  // toggle reads as a quiet link inside the subtitle; the reveal inherits the
  // subtitle's muted tone so it extends the sentence rather than shouting.
  heroPitchToggle: {
    background: 'none',
    border: 'none',
    padding: '0 0 0 6px',
    color: '#c4b5fd',
    fontWeight: 700,
    fontSize: 'inherit',
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  heroPitchReveal: {
    color: 'rgba(255,255,255,0.72)',
  },
  heroHighlight: {
    color: '#c4b5fd',
    fontWeight: 600,
  },
  heroActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    // the chips row used to hold subtitle/buttons apart; without it the
    // subtitle's 14px alone reads cramped
    marginTop: 10,
  },
  heroEyebrowArrow: {
    marginLeft: 6,
    fontSize: '0.85rem',
    opacity: 0.85,
  },
  statsStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    flexWrap: 'wrap',
    padding: '18px 16px 4px',
    marginTop: 40,
    fontSize: 13,
    color: '#666',
    borderTop: '1px solid #eee',
  },
  statsStripItem: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 5,
  },
  statsStripNum: {
    color: '#111',
    fontWeight: 700,
    fontSize: 14,
  },
  statsStripDot: {
    color: '#ccc',
    fontSize: 14,
  },
  // ── Modal ─────────────────────────────────────────────────
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    position: 'relative',
    background: 'linear-gradient(160deg, #1e1e3a 0%, #2a1a50 100%)',
    borderRadius: 16,
    padding: '32px 28px',
    width: '100%',
    maxWidth: 420,
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  modalClose: {
    position: 'absolute',
    top: 12,
    right: 14,
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 4,
    lineHeight: 1,
  },
  modalAgentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 24,
  },
  modalSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  modalSectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  heroPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#0a0a0a',
    backgroundColor: '#fff',
    border: '1px solid #0a0a0a',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
  },
  heroSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#fff',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
  },
  // ── Content ───────────────────────────────────────────────
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '32px 20px 60px',
  },
  controls: {
    display: 'flex',
    gap: 15,
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  searchContainer: {
    flex: 1,
    minWidth: 250,
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 12px 12px 40px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: 6,
    outline: 'none',
    boxSizing: 'border-box',
  },
  sortContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  sortSelect: {
    padding: '12px 30px 12px 12px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: 6,
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  tagsRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  tagChip: {
    padding: '6px 14px',
    borderRadius: 16,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#666',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tagChipActive: {
    background: 'rgba(123, 31, 162, 0.1)',
    borderColor: '#7b1fa2',
    color: '#7b1fa2',
  },
  loadingText: {
    textAlign: 'center',
    color: '#666',
    padding: 40,
  },
  errorText: {
    textAlign: 'center',
    color: '#d32f2f',
    padding: 40,
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
  },
  // ── Agent Cards ───────────────────────────────────────────
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  // ── Hall of Fame ──────────────────────────────────────────
  hofSection: {
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 12,
    padding: '16px 18px',
    marginBottom: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  hofTitle: {
    fontSize: '1.05rem',
    fontWeight: 800,
    color: '#111',
    marginBottom: 12,
  },
  hofColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
  },
  hofColumn: {
    minWidth: 0,
  },
  hofColumnLabel: {
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    color: '#7b1fa2',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  hofRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 6px',
    borderRadius: 8,
    cursor: 'pointer',
  },
  hofRank: {
    fontSize: 16,
    flexShrink: 0,
  },
  hofAvatar: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: 'rgba(123, 31, 162, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  hofName: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#111',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
  },
  hofStat: {
    fontSize: '0.78rem',
    color: '#666',
    fontWeight: 600,
    flexShrink: 0,
  },
  // Ticker styles moved into components/WorldTicker.js (shared with /explore).
  card: {
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 12,
    padding: 16,
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    // Anchor for the 🔥 fuel-jar overlay (and any future card badges).
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    marginBottom: 10,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'rgba(123, 31, 162, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  cardArt: {
    width: '100%',
    aspectRatio: '1 / 1',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 10,
    background: 'rgba(123, 31, 162, 0.1)',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarEmoji: {
    fontSize: 22,
  },
  cardTitleSection: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    color: '#111',
    fontWeight: 600,
    fontSize: 15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
    flexWrap: 'wrap',
  },
  cardPlatform: {
    color: '#888',
    fontSize: 12,
  },
  cardTypeBadgeImported: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#7b1fa2',
    background: 'rgba(123, 31, 162, 0.08)',
    padding: '2px 7px',
    borderRadius: 10,
    whiteSpace: 'nowrap',
  },
  cardTypeBadgeConnected: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#2563eb',
    background: 'rgba(37, 99, 235, 0.08)',
    padding: '2px 7px',
    borderRadius: 10,
    whiteSpace: 'nowrap',
  },
  cardDescription: {
    color: '#444',
    fontSize: 13,
    lineHeight: 1.4,
    marginBottom: 10,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardTags: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  cardTag: {
    padding: '3px 8px',
    borderRadius: 10,
    background: 'rgba(123, 31, 162, 0.1)',
    color: '#7b1fa2',
    fontSize: 11,
  },
  cardStats: {
    display: 'flex',
    gap: 16,
    color: '#888',
    fontSize: 12,
    marginBottom: 10,
  },

  // --- Reaction chips (compact 👍/👎 row) ---
  reactionRow: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
    marginBottom: 6,
    alignItems: 'center',
    // Wrap is a safety net for the narrowest cards: the share chip grows when
    // it flips to "Link copied!", and a third chip must never push the row
    // past the card edge on a small phone.
    flexWrap: 'wrap',
  },
  reactionChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 14,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  reactionChipFeatured: {
    borderColor: '#d4a017',
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#6b4f00',
  },
  reactionChipActiveLike: {
    background: 'rgba(76, 175, 80, 0.15)',
    borderColor: '#4caf50',
    color: '#2e7d32',
  },
  reactionChipActiveDislike: {
    background: 'rgba(244, 67, 54, 0.1)',
    borderColor: '#f44336',
    color: '#c62828',
  },
  reactionCount: {
    fontWeight: 600,
    fontSize: 12,
  },
  // 🔗 Share chip — same pill language as the reaction chips, pushed to the
  // row's trailing edge. padding is tightened because it carries an icon and
  // (usually) no label, so the default 4px/10px would read as a lopsided pill.
  shareChip: {
    marginLeft: 'auto',
    padding: '4px 8px',
    gap: 5,
    color: '#666',
  },
  shareChipCopied: {
    background: 'rgba(33, 150, 243, 0.12)',
    borderColor: '#2196f3',
    color: '#1565c0',
    fontWeight: 600,
  },
  // --- Capture CTA (full-width button above Launch) ---
  captureCTA: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    padding: '9px 16px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    boxSizing: 'border-box',
  },
  captureCTAFeatured: {
    borderColor: '#d4a017',
    background: 'rgba(255, 255, 255, 0.85)',
    color: '#6b4f00',
  },
  captureCTAActive: {
    background: 'linear-gradient(90deg, #f7b733, #fc4a1a)',
    borderColor: 'transparent',
    color: '#fff',
  },

  // --- Featured (golden) card ---
  featuredSection: {
    marginBottom: 28,
  },
  featuredLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#b8860b',
    fontWeight: 700,
    letterSpacing: 0.3,
    fontSize: 13,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  featuredStar: {
    color: '#f7b733',
    fontSize: 18,
  },
  featuredWeek: {
    color: '#999',
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: 0,
  },
  featuredCard: {
    position: 'relative',
    background: 'linear-gradient(135deg, #fffaeb 0%, #fff2c8 100%)',
    border: '1px solid #d4a017',
    borderRadius: 12,
    padding: 16,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(212, 160, 23, 0.18)',
    overflow: 'hidden',
    maxWidth: 360,
    margin: '0 auto',
  },
  featuredHeader: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    marginBottom: 10,
    position: 'relative',
  },
  featuredAvatar: {
    width: 108,
    height: 108,
    borderRadius: 14,
    background: '#fff',
    border: '2px solid #d4a017',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  featuredAvatarEmoji: {
    fontSize: 22,
  },
  featuredTitleSection: {
    flex: 1,
    minWidth: 0,
  },
  featuredName: {
    color: '#3d2a00',
    fontWeight: 700,
    fontSize: 16,
  },
  featuredPlatform: {
    color: '#6b4f00',
    fontSize: 12,
    marginTop: 2,
  },
  featuredReason: {
    color: '#3d2a00',
    fontSize: 12,
    marginTop: 4,
    fontStyle: 'italic',
  },
  featuredTags: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 8,
    position: 'relative',
  },
  featuredTag: {
    padding: '2px 8px',
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.7)',
    color: '#6b4f00',
    fontSize: 11,
    fontWeight: 600,
    border: '1px solid rgba(212, 160, 23, 0.4)',
  },
  featuredStats: {
    display: 'flex',
    gap: 14,
    color: '#6b4f00',
    fontSize: 12,
    marginBottom: 6,
    position: 'relative',
    fontWeight: 600,
  },
  featuredDescription: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px solid rgba(212, 160, 23, 0.3)',
    color: '#3d2a00',
    fontSize: 13,
    lineHeight: 1.5,
    position: 'relative',
  },

  inviteSection: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTop: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  inviteInput: {
    width: '100%',
    minWidth: 0,
    padding: '9px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  inviteResolved: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 12,
    color: '#15803d',
    fontWeight: 600,
  },
  inviteResolvedCheck: {
    fontSize: 13,
    color: '#15803d',
  },
  inviteInvalid: {
    fontSize: 12,
    color: '#b91c1c',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  inviteHint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    fontSize: 11.5,
    color: '#888',
    lineHeight: 1.4,
    textAlign: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  inviteHintIcon: {
    fontSize: 12,
    lineHeight: 1.4,
    flexShrink: 0,
  },
  inviteHintCode: {
    background: '#f4f4f5',
    padding: '1px 5px',
    borderRadius: 3,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    fontSize: 10.5,
    color: '#be185d',
    border: '1px solid #e4e4e7',
  },
  inviteHintLink: {
    color: '#7b1fa2',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  successBanner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '12px 0 4px',
  },
  successCheck: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.35)',
  },
  successText: {
    fontSize: 14,
    fontWeight: 700,
    color: '#22c55e',
  },
  successGoButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(34, 197, 94, 0.3)',
    transition: 'all 0.2s',
  },
  successDismiss: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.45)',
    fontSize: 12,
    cursor: 'pointer',
    padding: '2px 8px',
  },
  createWithAgentButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #7b1fa2, #4f46e5)',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 3px 10px rgba(123, 31, 162, 0.3)',
    transition: 'all 0.2s',
  },
  launchButton: {
    width: '100%',
    padding: '11px 16px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
    letterSpacing: 0.3,
  },
  launchDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '10px 0',
  },
  launchDividerLine: {
    flex: 1,
    height: 1,
    background: '#e4e4e7',
  },
  launchDividerText: {
    fontSize: 11.5,
    color: '#999',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  inviteConfirmButton: {
    padding: '9px 22px',
    borderRadius: 6,
    border: 'none',
    background: '#7b1fa2',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  footerCta: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    padding: '22px 26px',
    borderRadius: 14,
    background: 'linear-gradient(135deg, rgba(123, 31, 162, 0.06) 0%, rgba(79, 70, 229, 0.06) 100%)',
    border: '1px solid #e4e4e7',
    cursor: 'pointer',
    outline: 'none',
  },
  footerCtaIcon: {
    fontSize: 28,
    flexShrink: 0,
  },
  footerCtaText: {
    flex: 1,
    minWidth: 0,
  },
  footerCtaTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111',
    marginBottom: 4,
  },
  footerCtaSubtitle: {
    fontSize: 13.5,
    color: '#555',
    lineHeight: 1.5,
  },
  footerCtaArrow: {
    fontSize: 22,
    color: '#7b1fa2',
    fontWeight: 700,
    flexShrink: 0,
  },
};

export default MarketplacePage;
